import type { ClientAccessSettings } from "@repo/shared/types";
import { useState } from "react";
import { valuePickerLabels } from "@/components/clients/filters";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ValuePickerDialog } from "@/components/ui/value-picker-dialog";
import { ValuePickerTrigger } from "@/components/ui/value-picker-trigger";
import { useTeams } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./client-access-fields.i18n";

type Props = {
  settings: ClientAccessSettings;
  /** Owner or Admin (or creating a new client): may change the Private flag and access teams. */
  canManage: boolean;
  isPrivate: boolean;
  teamIds: string[];
  onPrivateChange: (value: boolean) => void;
  onTeamsChange: (ids: string[]) => void;
};

/**
 * QL-380 — the client's access fields, placed directly under Client Owner (Decision 6).
 * The Private checkbox shows while Private clients is on (owner or Admin). The teams picker
 * always shows: while team access is on the teams decide who sees the client, so only the
 * owner or Admin may change them; while it is off they are labels anyone editing may set.
 * The server enforces the same rule.
 */
export function ClientAccessFields({
  settings,
  canManage,
  isPrivate,
  teamIds,
  onPrivateChange,
  onTeamsChange,
}: Props) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const readOnly = t.readOnly;
  const { teams, isLoading: teamsLoading } = useTeams();
  const [pickerOpen, setPickerOpen] = useState(false);

  const teamAccess = settings.teamAccessEnabled;
  const canEditTeams = !teamAccess || canManage;
  const teamsLabel = teamAccess ? t.teamsWithAccess : t.teams;

  // Nothing to pick and nothing to clear: with team access off an empty picker is only noise.
  // With team access on it stays, because it explains that the client is owner-only.
  const hideTeams = !teamsLoading && teams.length === 0 && !teamAccess && teamIds.length === 0;

  const options = teams.map((team) => ({ value: team.id, label: team.name }));
  const selectedNames = options.filter((o) => teamIds.includes(o.value)).map((o) => o.label);

  return (
    <div className="space-y-4">
      {settings.privateEnabled && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id="client-private"
              checked={isPrivate}
              disabled={!canManage}
              onCheckedChange={(v) => onPrivateChange(v === true)}
            />
            <Label htmlFor="client-private">{t.private}</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            {!canManage ? readOnly : teamAccess ? t.privateWinsHint : t.privateHint}
          </p>
        </div>
      )}

      {!hideTeams && (
        <div className="space-y-1">
          <Label htmlFor="client-teams">{teamsLabel}</Label>
          <ValuePickerTrigger
            id="client-teams"
            value={selectedNames.length > 0 ? selectedNames.join(", ") : null}
            placeholder={teamAccess ? t.teamsPlaceholderAccess : t.teamsPlaceholder}
            disabled={!canEditTeams}
            onOpen={() => setPickerOpen(true)}
            onClear={() => onTeamsChange([])}
          />
          <p className="text-xs text-muted-foreground">
            {!canEditTeams ? readOnly : teamAccess ? t.teamsAccessHint : t.teamsHint}
          </p>
          <ValuePickerDialog
            open={pickerOpen}
            options={options}
            selectedValues={teamIds}
            labels={valuePickerLabels(userLanguage, teamsLabel)}
            onClose={() => setPickerOpen(false)}
            onSave={(ids) => {
              onTeamsChange(ids);
              setPickerOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
